import {
  CfDataGridForStoreDataProps,
  CfDataGridProps,
  CfDataGridTableViewDefinition,
  NavigableElement,
} from '../../../CfDataGridProps';
import { getFilterText, getHasItems, getItemIds, getItems } from '../../../utility/data-grid-props';
import { CfDataGridNoResultsFound } from '../../DataGridNoResultsFound/CfDataGridNoResultsFound';
import { CfDataGridRowByItemId } from '../row/CfDataGridRowByItemId';
import { CfDataGridRows } from '../row/CfDataGridRows';
import { StyledGridBody } from './body.styles';
import { createValueGetters } from './common';

export function CfDataGridTableViewBody<TItem, TAppState>(props: {
  dataGridProps: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>;
  tableViewDefinition: CfDataGridTableViewDefinition<TItem>;
  setNavigableElement: (navigableElement: NavigableElement, rowIndex: number, columnIndex: number) => void;
}): JSX.Element {
  const valueGetters = createValueGetters(props.tableViewDefinition.columns);

  const dataGridProps = props.dataGridProps;
  const filterText = getFilterText(dataGridProps);

  if (!getHasItems(dataGridProps)) {
    return (
      <StyledGridBody>
        <CfDataGridNoResultsFound
          message={props.dataGridProps.noItemsMessage}
          sx={props.dataGridProps.noItemsMessageSx}
          displayMode='table'
        />
      </StyledGridBody>
    );
  } else if ('data' in dataGridProps) {
    const items = getItems(dataGridProps.data);
    const itemCount = items.filter((i) => !i.isLoading).length;
    return (
      <StyledGridBody sx={props.tableViewDefinition.tableBodySx}>
        {items.map(({ item }, index: number) => (
          <CfDataGridRows
            key={item ? dataGridProps.selectId(item) : index}
            item={item}
            previousItem={items[index - 1]?.item}
            tableViewDefinition={props.tableViewDefinition}
            valueGetters={valueGetters}
            itemIndex={index}
            itemCount={itemCount}
            onItemClicked={dataGridProps.onItemClicked}
            hoverStyle={dataGridProps.hoverStyle}
            cursorStyle={dataGridProps.cursorStyle}
            setNavigableElement={props.setNavigableElement}
            filterText={filterText}
            recomputeRowHeights={undefined}
            recomputeRowHeight={undefined}
          />
        ))}
      </StyledGridBody>
    );
  } else {
    const itemIds = getItemIds(dataGridProps.storeData);
    const itemCount = itemIds.filter((i) => !i.isLoading).length;
    return (
      <StyledGridBody sx={props.tableViewDefinition.tableBodySx}>
        {itemIds.map(({ itemId }, index: number) => (
          <CfDataGridRowByItemId<TItem, TAppState>
            key={itemId ?? index}
            itemId={itemId}
            previousItemId={itemIds[index - 1]?.itemId}
            itemSelector={dataGridProps.storeData.itemSelector}
            useSelector={dataGridProps.storeData.useSelector}
            tableViewDefinition={props.tableViewDefinition}
            valueGetters={valueGetters}
            itemIndex={index}
            itemCount={itemCount}
            onItemClicked={dataGridProps.onItemClicked}
            hoverStyle={dataGridProps.hoverStyle}
            cursorStyle={dataGridProps.cursorStyle}
            setNavigableElement={props.setNavigableElement}
            filterText={filterText}
            recomputeRowHeights={undefined}
            recomputeRowHeight={undefined}
          />
        ))}
      </StyledGridBody>
    );
  }
}
